/*
 * TourStock cable builder artwork. Draws connectors and whole cables as
 * inline SVG so customers can pick by what a connector looks like, not by its
 * model number, and see the cable take shape as they design it.
 *
 * Everything is drawn from a connector's `look` (shape, gender, finish,
 * right-angle, poles) in the public catalogue: there are no image files to
 * host, no model name is needed to draw one, and a new connector in an
 * existing family draws itself.
 *
 *   CableArt.defs()                      shared gradients; put in the page once
 *   CableArt.connector(look, opts)       { svg, w, h, ax, ay } one connector, cable on the left
 *   CableArt.connectorSvg(look, opts)    a stand-alone <svg> for pickers
 *   CableArt.diagram(model, opts)        a whole cable or system as an <svg>
 *
 * Works in the browser and in Node (for tests).
 */
(function (root, factory) {
  if (typeof module === "object" && module.exports) module.exports = factory();
  else root.CableArt = factory();
})(
  typeof self !== "undefined"
    ? self
    : typeof globalThis !== "undefined"
      ? globalThis
      : this,
  function () {
    "use strict";

    var W = 150; // a connector is drawn in a 150 x 100 box, mating end on the right
    var H = 100;

    var INK = "#1b1b1b";
    var BOOT_COLOURS = {
      black: "#232323",
      brown: "#7b4a2a",
      red: "#d0312d",
      orange: "#ee7c14",
      yellow: "#f2c200",
      green: "#2f9e44",
      blue: "#2a64c5",
      violet: "#7a4ab5",
      purple: "#7a4ab5",
      grey: "#8a8f94",
      gray: "#8a8f94",
      white: "#f4f4f1",
      pink: "#e87aa8",
    };

    function esc(s) {
      return String(s)
        .replace(/&/g, "&amp;")
        .replace(/</g, "&lt;")
        .replace(/>/g, "&gt;")
        .replace(/"/g, "&quot;");
    }

    /** The colour a boot or ring part is named after ("Red boot for XX series"), or null. */
    function colourOf(name) {
      var m = String(name || "")
        .toLowerCase()
        .match(
          /\b(black|brown|red|orange|yellow|green|blue|violet|purple|grey|gray|white|pink)\b/,
        );
      return m ? BOOT_COLOURS[m[1]] : null;
    }

    function defs() {
      return (
        '<svg width="0" height="0" style="position:absolute" aria-hidden="true" focusable="false"><defs>' +
        '<linearGradient id="ca-metal" x1="0" y1="0" x2="0" y2="1"><stop offset="0" stop-color="#f4f5f6"/><stop offset=".42" stop-color="#bfc4c9"/><stop offset="1" stop-color="#868c93"/></linearGradient>' +
        '<linearGradient id="ca-black" x1="0" y1="0" x2="0" y2="1"><stop offset="0" stop-color="#5a5a5a"/><stop offset=".4" stop-color="#2b2b2b"/><stop offset="1" stop-color="#101010"/></linearGradient>' +
        '<linearGradient id="ca-gold" x1="0" y1="0" x2="0" y2="1"><stop offset="0" stop-color="#f7de82"/><stop offset="1" stop-color="#c18f12"/></linearGradient>' +
        '<linearGradient id="ca-clear" x1="0" y1="0" x2="0" y2="1"><stop offset="0" stop-color="#ffffff" stop-opacity=".95"/><stop offset="1" stop-color="#cfd5da" stop-opacity=".9"/></linearGradient>' +
        '<radialGradient id="ca-face" cx=".4" cy=".35" r=".8"><stop offset="0" stop-color="#4a4a4a"/><stop offset="1" stop-color="#101010"/></radialGradient>' +
        "</defs></svg>"
      );
    }

    function paint(finish) {
      return finish === "black" ? "url(#ca-black)" : "url(#ca-metal)";
    }

    function line(c) {
      return c === "black" ? "#000" : "#6b7178";
    }

    function rect(x, y, w, h, rx, fill, stroke, sw) {
      return (
        '<rect x="' +
        x +
        '" y="' +
        y +
        '" width="' +
        w +
        '" height="' +
        h +
        '" rx="' +
        (rx || 0) +
        '" fill="' +
        fill +
        '"' +
        (stroke
          ? ' stroke="' + stroke + '" stroke-width="' + (sw || 1) + '"'
          : "") +
        "/>"
      );
    }

    function circ(cx, cy, r, fill, stroke, sw) {
      return (
        '<circle cx="' +
        cx +
        '" cy="' +
        cy +
        '" r="' +
        r +
        '" fill="' +
        fill +
        '"' +
        (stroke
          ? ' stroke="' + stroke + '" stroke-width="' + (sw || 1) + '"'
          : "") +
        "/>"
      );
    }

    function boot(x0, y0, x1, ya, yb, fill) {
      // tapers from the cable (x0, y0 +/- 7) up to the body (x1, ya..yb)
      return (
        '<path d="M' +
        x0 +
        "," +
        (y0 - 7) +
        " L" +
        x1 +
        "," +
        ya +
        " L" +
        x1 +
        "," +
        yb +
        " L" +
        x0 +
        "," +
        (y0 + 7) +
        ' Z" fill="' +
        fill +
        '" stroke="#000" stroke-opacity=".35"/>'
      );
    }

    function pin(cx, cy, male, finishGold) {
      var metal = finishGold ? "url(#ca-gold)" : "url(#ca-metal)";
      return male
        ? circ(cx, cy, 2.7, metal, "#555", 0.6)
        : circ(cx, cy, 3.1, "#050505", metal, 1.1);
    }

    function pinLayout(shape, poles) {
      // (dx, dy) from the centre of the face, in the face's own units
      if (shape === "xlr5")
        return [
          [-8, -7],
          [-2.5, -11],
          [4, -11],
          [10, -7],
          [1, 7],
        ];
      if (shape === "minixlr")
        return [
          [-5, -4],
          [5, -4],
          [0, 5],
        ];
      if (shape === "speakon") {
        if (poles === 2)
          return [
            [-5, 0],
            [5, 0],
          ];
        if (poles === 8)
          return [
            [-8, -8],
            [0, -8],
            [8, -8],
            [-8, 0],
            [8, 0],
            [-8, 8],
            [0, 8],
            [8, 8],
          ];
        return [
          [-5, -5],
          [5, -5],
          [-5, 5],
          [5, 5],
        ];
      }
      return [
        [-8, -6],
        [8, -6],
        [0, 8],
      ];
    }

    /** Face of a round connector seen at a slight angle: an ellipse with contacts. */
    function roundFace(cx, cy, rx, ry, look, shape, scale) {
      var male = look.gender === "male";
      var gold = !!look.gold;
      var out =
        '<ellipse cx="' +
        cx +
        '" cy="' +
        cy +
        '" rx="' +
        rx +
        '" ry="' +
        ry +
        '" fill="url(#ca-face)" stroke="' +
        line(look.finish) +
        '" stroke-width="1.2"/>';
      if (look.gender === "female" || shape === "speakon") {
        out +=
          '<ellipse cx="' +
          cx +
          '" cy="' +
          cy +
          '" rx="' +
          (rx - 3) +
          '" ry="' +
          (ry - 4) +
          '" fill="none" stroke="#777" stroke-width=".8"/>';
      }
      pinLayout(shape, look.poles).forEach(function (p) {
        out += pin(
          cx + p[0] * (rx / 10) * (scale || 1),
          cy + p[1] * (ry / 24) * 1.9 * (scale || 1),
          male,
          gold,
        );
      });
      return out;
    }

    function grip(x, y0, y1, n, gap) {
      var out = "";
      for (var i = 0; i < n; i++) {
        out +=
          '<line x1="' +
          (x + i * gap) +
          '" y1="' +
          y0 +
          '" x2="' +
          (x + i * gap) +
          '" y2="' +
          y1 +
          '" stroke="#000" stroke-opacity=".22" stroke-width="1.4"/>';
      }
      return out;
    }

    var DRAW = {};

    DRAW.xlr = function (look, o) {
      var body = paint(look.finish);
      var bootFill = o.boot || "#1d1d1d";
      var five = look.shape === "xlr5";
      if (look.rightAngle) {
        // the cable leaves from underneath: a boot that bends down from the barrel
        var a =
          '<path d="M36,62 L68,62 L61,88 L43,88 Z" fill="' +
          bootFill +
          '" stroke="#000" stroke-opacity=".35"/>';
        a += rect(34, 14, 62, 50, 9, body, line(look.finish), 1.2);
        a += grip(44, 21, 57, 4, 5);
        a += rect(70, 7, 18, 9, 2, body, line(look.finish), 1);
        if (o.ring) a += rect(34, 14, 7, 50, 3, o.ring);
        a += roundFace(100, 39, 11, 25, look, five ? "xlr5" : "xlr3");
        return { svg: a, ax: 52, ay: 100 };
      }
      var s = boot(8, 50, 34, 31, 69, bootFill);
      s += rect(34, 25, 62, 50, 9, body, line(look.finish), 1.2);
      s += grip(44, 32, 68, 4, 5);
      s += rect(70, 18, 18, 9, 2, body, line(look.finish), 1);
      if (o.ring) s += rect(34, 25, 7, 50, 3, o.ring);
      s += roundFace(100, 50, 11, 25, look, five ? "xlr5" : "xlr3");
      return { svg: s, ax: 0, ay: 50 };
    };

    DRAW.minixlr = function (look, o) {
      var body = paint(look.finish);
      var s = boot(8, 50, 40, 38, 62, o.boot || "#1d1d1d");
      s += rect(40, 32, 44, 36, 8, body, line(look.finish), 1.1);
      s += grip(48, 38, 62, 3, 5);
      s += roundFace(90, 50, 8, 17, look, "minixlr");
      return { svg: s, ax: 0, ay: 50 };
    };

    function jackBody(look, o, small) {
      var body = paint(look.finish);
      var stereo = look.poles === 3 || look.shape === "jack35";
      var r = small ? 5.5 : 9;
      var s = "";
      if (look.rightAngle) {
        s +=
          '<path d="M36,100 L36,86 L52,86 L52,100 Z" fill="' +
          (o.boot || "#1d1d1d") +
          '"/>';
        s += rect(22, 22, 44, 66, 8, body, line(look.finish), 1.2);
        s += grip(30, 28, 82, 3, 6);
        s += rect(66, 50 - r - 3, 14, 2 * r + 6, 3, body, line(look.finish), 1);
        s += rect(80, 50 - r, 40, 2 * r, r, "url(#ca-metal)", "#6b7178", 1);
        var bx = 98;
        s += rect(bx, 50 - r, 4, 2 * r, 0, "#111");
        if (stereo) s += rect(bx + 12, 50 - r, 4, 2 * r, 0, "#111");
        s +=
          '<path d="M' +
          (stereo ? 118 : 106) +
          "," +
          (50 - r) +
          " L140," +
          (50 - 3) +
          " Q144,50 140," +
          (50 + 3) +
          " L" +
          (stereo ? 118 : 106) +
          "," +
          (50 + r) +
          ' Z" fill="url(#ca-metal)" stroke="#6b7178"/>';
        return { svg: s, ax: 44, ay: 100 };
      }
      s += boot(8, 50, 38, 36, 64, o.boot || "#1d1d1d");
      s += rect(38, 32, 38, 36, 7, body, line(look.finish), 1.2);
      s += grip(46, 38, 62, 4, 6);
      if (o.ring) s += rect(38, 32, 6, 36, 3, o.ring);
      s += rect(76, 50 - r, 40, 2 * r, r, "url(#ca-metal)", "#6b7178", 1);
      var b1 = stereo ? 96 : 100;
      s += rect(b1, 50 - r, 4, 2 * r, 0, "#111");
      if (stereo) s += rect(110, 50 - r, 4, 2 * r, 0, "#111");
      var tipFrom = stereo ? 114 : 104;
      s +=
        '<path d="M' +
        tipFrom +
        "," +
        (50 - r) +
        " L142," +
        (50 - 3) +
        " Q147,50 142," +
        (50 + 3) +
        " L" +
        tipFrom +
        "," +
        (50 + r) +
        ' Z" fill="url(#ca-metal)" stroke="#6b7178"/>';
      return { svg: s, ax: 0, ay: 50 };
    }

    DRAW.jack = function (look, o) {
      return jackBody(look, o, false);
    };
    DRAW.jack35 = function (look, o) {
      return jackBody(look, o, true);
    };
    DRAW.bantam = function (look, o) {
      return jackBody(
        { finish: look.finish, poles: 3, shape: "jack" },
        o,
        true,
      );
    };

    DRAW.phono = function (look, o) {
      var chassis = look.chassis;
      var band =
        o.ring ||
        colourOf(look.title + " " + (look.tags || []).join(" ")) ||
        "#c8302a";
      var s = chassis ? "" : boot(8, 50, 36, 38, 62, o.boot || "#1d1d1d");
      s += rect(
        chassis ? 20 : 36,
        34,
        34,
        32,
        6,
        paint(look.finish),
        line(look.finish),
        1.2,
      );
      s += rect(chassis ? 54 : 70, 34, 6, 32, 0, band);
      s += rect(
        chassis ? 60 : 76,
        38,
        28,
        24,
        5,
        "url(#ca-metal)",
        "#6b7178",
        1,
      );
      s += grip(chassis ? 66 : 82, 41, 59, 4, 6);
      s += rect(
        chassis ? 88 : 104,
        46,
        26,
        8,
        4,
        "url(#ca-gold)",
        "#9a7410",
        1,
      );
      return { svg: s, ax: 0, ay: 50 };
    };

    DRAW.speakon = function (look, o) {
      var body = paint(look.finish === "black" ? "black" : "nickel");
      var s = boot(8, 50, 34, 30, 70, o.boot || "#1d1d1d");
      s += rect(34, 22, 56, 56, 11, body, line(look.finish), 1.2);
      s += grip(44, 30, 70, 4, 5);
      s +=
        '<path d="M60,14 h18 l3,10 h-24 Z" fill="' +
        body +
        '" stroke="' +
        line(look.finish) +
        '"/>';
      s +=
        '<rect x="86" y="30" width="14" height="40" rx="5" fill="url(#ca-metal)" stroke="#6b7178"/>';
      s += roundFace(
        104,
        50,
        12,
        26,
        { gender: "male", finish: look.finish, poles: look.poles || 4 },
        "speakon",
        0.9,
      );
      return { svg: s, ax: 0, ay: 50 };
    };

    DRAW.bnc = function (look, o) {
      var s = boot(8, 50, 36, 37, 63, o.boot || "#1d1d1d");
      s += rect(36, 34, 34, 32, 6, "url(#ca-metal)", "#6b7178", 1.2);
      s += grip(44, 38, 62, 4, 6);
      s += rect(70, 30, 38, 40, 6, "url(#ca-metal)", "#6b7178", 1.2);
      s +=
        '<rect x="86" y="30" width="5" height="12" fill="#222"/><rect x="86" y="58" width="5" height="12" fill="#222"/>';
      s += rect(108, 46, 26, 8, 4, "url(#ca-gold)", "#9a7410", 1);
      return { svg: s, ax: 0, ay: 50 };
    };

    DRAW.coax = function (look, o) {
      var s = DRAW.bnc(look, o).svg;
      s += grip(76, 33, 67, 5, 5);
      return { svg: s, ax: 0, ay: 50 };
    };
    DRAW.tnc = DRAW.coax;
    DRAW.ntype = DRAW.coax;

    function contacts(x, y, n, gap) {
      var out = "";
      for (var i = 0; i < n; i++)
        out +=
          '<rect x="' +
          (x + i * gap) +
          '" y="' +
          y +
          '" width="3" height="10" fill="url(#ca-gold)"/>';
      return out;
    }

    DRAW.rj45 = function (look, o) {
      var s =
        '<path d="M8,43 L40,35 L40,65 L8,57 Z" fill="url(#ca-clear)" stroke="#9aa3aa"/>';
      s += rect(40, 28, 66, 44, 4, "url(#ca-clear)", "#9aa3aa", 1.2);
      s += rect(60, 20, 26, 9, 2, "url(#ca-clear)", "#9aa3aa", 1);
      s += contacts(112 - 36, 54, 8, 5);
      s += rect(104, 34, 6, 32, 0, "#e8ecef");
      return { svg: s, ax: 0, ay: 50 };
    };

    DRAW.ethercon = function (look, o) {
      var s = boot(8, 50, 34, 30, 70, o.boot || "#1d1d1d");
      s += rect(34, 22, 52, 56, 11, paint(look.finish), line(look.finish), 1.2);
      s += grip(44, 30, 70, 4, 5);
      s += rect(86, 30, 40, 40, 4, "url(#ca-clear)", "#9aa3aa", 1.2);
      s += rect(98, 24, 18, 8, 2, "url(#ca-clear)", "#9aa3aa", 1);
      s += contacts(92, 56, 8, 4.2);
      return { svg: s, ax: 0, ay: 50 };
    };

    DRAW.optical = function (look, o) {
      var s = boot(8, 50, 34, 34, 66, o.boot || "#1d1d1d");
      s += rect(
        34,
        26,
        60,
        48,
        9,
        paint(look.finish === "black" ? "black" : "nickel"),
        "#6b7178",
        1.2,
      );
      s += grip(44, 32, 68, 4, 5);
      s += rect(94, 34, 22, 32, 5, "url(#ca-metal)", "#6b7178", 1);
      s +=
        circ(124, 42, 6, "#111", "#bbb", 1.2) +
        circ(124, 58, 6, "#111", "#bbb", 1.2);
      return { svg: s, ax: 0, ay: 50 };
    };

    DRAW.pole19 = function (look, o) {
      var s = boot(8, 50, 28, 26, 74, o.boot || "#1d1d1d");
      s += rect(28, 14, 70, 72, 16, paint(look.finish), line(look.finish), 1.2);
      s += grip(40, 24, 76, 4, 6);
      s +=
        '<path d="M70,8 h16 l3,8 h-22 Z" fill="url(#ca-metal)" stroke="#6b7178"/>';
      s +=
        '<ellipse cx="106" cy="50" rx="20" ry="36" fill="url(#ca-face)" stroke="#6b7178" stroke-width="1.2"/>';
      var male = look.gender === "male";
      var pts = [[0, 0]];
      var i;
      for (i = 0; i < 6; i++)
        pts.push([Math.cos(i * 1.0472) * 6, Math.sin(i * 1.0472) * 11]);
      for (i = 0; i < 12; i++)
        pts.push([
          Math.cos(i * 0.5236 + 0.26) * 13,
          Math.sin(i * 0.5236 + 0.26) * 25,
        ]);
      pts.slice(0, 19).forEach(function (p) {
        s += pin(106 + p[0], 50 + p[1], male, true);
      });
      return { svg: s, ax: 0, ay: 50 };
    };

    DRAW.multipin = function (look, o) {
      var male = look.gender === "male";
      var s = boot(8, 50, 30, 26, 74, o.boot || "#1d1d1d");
      s += rect(
        30,
        14,
        64,
        72,
        8,
        paint(look.finish === "black" ? "black" : "nickel"),
        "#6b7178",
        1.2,
      );
      s += grip(40, 22, 78, 4, 6);
      var lever = /actuating|lever/i.test(
        (look.tags || []).join(" ") + look.title,
      );
      if (lever) s += rect(46, 8, 38, 8, 3, "url(#ca-metal)", "#6b7178", 1);
      s +=
        '<path d="M94,20 L124,26 L124,74 L94,80 Z" fill="url(#ca-face)" stroke="#6b7178" stroke-width="1.2"/>';
      var rows = 3;
      var cols = 5;
      for (var r = 0; r < rows; r++)
        for (var c = 0; c < cols; c++) {
          s += pin(
            100 + c * 4.6 + r * 0.8,
            33 + r * 14 - c * 0.5 + 2,
            male,
            !!look.gold,
          );
        }
      return { svg: s, ax: 0, ay: 50 };
    };

    DRAW.banana = function (look, o) {
      var s = boot(8, 50, 34, 38, 62, o.boot || "#1d1d1d");
      var c = colourOf(look.title) || "#c8302a";
      s += rect(34, 30, 36, 40, 7, c, "#00000055", 1);
      s += rect(70, 33, 54, 8, 4, "url(#ca-gold)", "#9a7410", 1);
      s += rect(70, 59, 54, 8, 4, "url(#ca-gold)", "#9a7410", 1);
      return { svg: s, ax: 0, ay: 50 };
    };

    DRAW.spade = function (look, o) {
      var s = boot(8, 50, 34, 38, 62, o.boot || "#1d1d1d");
      s += rect(34, 34, 34, 32, 6, "#c8302a", "#00000055", 1);
      s +=
        '<path d="M68,38 h30 l12,-6 v14 l-12,4 h-30 Z M68,62 h30 l12,6 v-14 l-12,-4 h-30 Z" fill="url(#ca-gold)" stroke="#9a7410"/>';
      return { svg: s, ax: 0, ay: 50 };
    };

    DRAW.stagebox = function () {
      return { svg: "", ax: 0, ay: 50 };
    };

    DRAW.generic = function (look, o) {
      var s = boot(8, 50, 34, 32, 68, o.boot || "#1d1d1d");
      s += rect(34, 24, 70, 52, 9, paint(look.finish), line(look.finish), 1.2);
      s += grip(46, 32, 68, 4, 6);
      return { svg: s, ax: 0, ay: 50 };
    };

    function fam(shape) {
      if (shape === "xlr3" || shape === "xlr5") return "xlr";
      return DRAW[shape] ? shape : "generic";
    }

    /* ---------- drawn artwork supplied as files ---------- */

    var IMAGES = { base: "", map: {}, svg: {}, n: 0 };

    function familyOfMultipin(title) {
      var t = String(title || "");
      if (/^VDM OB Con/.test(t)) return "vdm-ob-con";
      if (/^VDM 150/.test(t)) return "vdm150";
      if (/^VDM/.test(t)) return "vdm";
      if (/^EDAC/.test(t)) return "edac";
      if (/^DL2/.test(t)) return "dl2";
      if (/^D25/.test(t)) return "d25";
      if (/^CEEP 13/.test(t)) return "ceep13";
      if (/^CEEP 39/.test(t)) return "ceep39";
      if (/^PA Con/.test(t)) return "pa-con";
      if (/^Amphenol/.test(t)) return "amphenol";
      return "other";
    }

    /**
     * The name of the picture for a connector, from its look. One picture
     * serves every part that looks the same (all the 3 pin nickel female XLRs).
     * docs/CONNECTOR_ART_BRIEF.md lists every key.
     */
    function keyFor(look) {
      var l = look || {};
      var title = l.title || "";
      var g =
        l.gender === "female" || /receptacle/i.test(title) ? "female" : "male";
      var finish = l.finish === "black" ? "black" : "nickel";
      var gold =
        finish === "black" &&
        l.gold &&
        /^(xlr3|xlr5|jack)$/.test(l.shape) &&
        !l.chassis
          ? "-gold"
          : "";
      var ra = l.rightAngle ? "-ra" : "";
      var panel = l.chassis ? "-panel" : "";
      switch (l.shape) {
        case "stagebox":
          return (
            "stagebox-" + parseInt(title, 10) + (l.rows === 2 ? "-2row" : "")
          );
        case "multipin":
          return (
            "multipin-" +
            familyOfMultipin(title) +
            "-" +
            g +
            "-" +
            (l.chassis ? "panel" : "cable") +
            ra
          );
        case "pole19":
          return "pole19-" + g + "-nickel";
        case "speakon":
          return "speakon-" + (l.poles || 4) + "pole-" + g + "-" + finish;
        case "jack":
          return (
            "jack-" +
            (l.poles === 3 ? "stereo" : "mono") +
            "-" +
            g +
            "-" +
            finish +
            gold +
            ra +
            panel
          );
        case "bantam":
          return "bantam-mono-" + g + "-" + finish;
        case "optical":
          return /Quad/.test(title)
            ? "optical-quad-" + g + "-" + finish
            : /Fibreco/.test(title)
              ? "optical-fibreco-" + g + "-" + finish + ra
              : "optical-duo-" + g + "-" + finish;
        default:
          return (
            (l.shape || "generic") + "-" + g + "-" + finish + gold + ra + panel
          );
      }
    }

    /**
     * Switches on supplied artwork. `manifest` is cable-conn-manifest.json:
     * [{ key, file, kind: 'svg'|'png', boot, ring, bootMask, ringMask, attach }].
     * `base` is the folder the files live in; `texts` maps a key to the SVG
     * source of pictures whose boot or ring can be recoloured. A key with no
     * picture keeps the drawn version.
     */
    /** `need(key)` is asked for a drawing's text the first time a recoloured boot or ring wants it. */
    function useImages(manifest, base, texts, need) {
      IMAGES.base = base || "";
      IMAGES.map = {};
      (manifest || []).forEach(function (e) {
        IMAGES.map[e.key] = e;
      });
      IMAGES.svg = texts || {};
      IMAGES.need = need || null;
    }

    function hasImage(look) {
      return !!IMAGES.map[keyFor(look)];
    }

    /**
     * Sets the fill of the group with this id. The supplied drawings carry a
     * part's colour as the group's own fill and shade it with black and white
     * overlays, so changing that one fill recolours the part and keeps its
     * shading.
     */
    function recolourGroup(svg, id, colour) {
      var open = new RegExp('<g\\b[^>]*\\bid="' + id + '"[^>]*>', "i").exec(
        svg,
      );
      if (!open) return svg;
      var tag = open[0];
      var next = /\bfill="[^"]*"/i.test(tag)
        ? tag.replace(/\bfill="[^"]*"/i, 'fill="' + colour + '"')
        : tag.replace(/<g\b/i, '<g fill="' + colour + '"');
      return (
        svg.slice(0, open.index) + next + svg.slice(open.index + tag.length)
      );
    }

    function imageArt(entry, o) {
      var attach = entry.attach;
      var ax = attach ? attach.x * W : 0;
      var ay = attach ? attach.y * H : 50;
      var s = "";
      var text = IMAGES.svg[entry.key];
      if (
        !text &&
        entry.kind === "svg" &&
        (entry.boot || entry.ring) &&
        (o.boot || o.ring) &&
        IMAGES.need
      )
        IMAGES.need(entry.key);
      if (entry.kind === "svg" && text && (o.boot || o.ring)) {
        var src = text
          .replace(/<\?xml[^>]*\?>/i, "")
          .replace(/<!DOCTYPE[^>]*>/i, "");
        if (o.boot) src = recolourGroup(src, "boot", o.boot);
        if (o.ring) src = recolourGroup(src, "ring", o.ring);
        src = src.replace(
          /<svg\b[^>]*>/i,
          '<svg x="0" y="0" width="' +
            W +
            '" height="' +
            H +
            '" viewBox="0 0 1800 1200">',
        );
        s = src.replace(/\sid="(boot|ring)"/gi, ' class="ca-$1"');
      } else {
        s =
          '<image href="' +
          esc(IMAGES.base + entry.file) +
          '" x="0" y="0" width="' +
          W +
          '" height="' +
          H +
          '"/>';
        [
          [o.boot, entry.bootMask],
          [o.ring, entry.ringMask],
        ].forEach(function (m) {
          if (!m[0] || !m[1]) return;
          IMAGES.n += 1;
          var id = "ca-mask-" + IMAGES.n;
          s +=
            '<mask id="' +
            id +
            '"><image href="' +
            esc(IMAGES.base + m[1]) +
            '" x="0" y="0" width="' +
            W +
            '" height="' +
            H +
            '"/></mask>' +
            '<rect x="0" y="0" width="' +
            W +
            '" height="' +
            H +
            '" fill="' +
            esc(m[0]) +
            '" mask="url(#' +
            id +
            ')"/>';
        });
      }
      // A family drawn smaller than 15 px/mm to fit its canvas (the opticalCON,
      // the 19 pole) is scaled back up about its cable entry in a diagram, so
      // sizes stay honest next to the other families, within what fits.
      var f = o.trueScale
        ? Math.min(Math.max(15 / (entry.scalePxPerMm || 15), 1), 1.4)
        : 1;
      if (f !== 1) {
        s =
          '<g transform="translate(' +
          ax +
          " " +
          ay +
          ") scale(" +
          f +
          ") translate(" +
          -ax +
          " " +
          -ay +
          ')">' +
          s +
          "</g>";
      }
      return { svg: s, ax: ax, ay: ay };
    }

    /**
     * One connector, drawn facing right with its cable on the left. `opts.boot`
     * and `opts.ring` are CSS colours (a coloured boot the customer chose).
     * `ax`,`ay` is where the cable meets it (right-angle connectors take the
     * cable from below). Uses the supplied picture when there is one.
     */
    function connector(look, opts) {
      var o = opts || {};
      look = look || { shape: "generic", finish: "nickel", gender: "male" };
      var entry = IMAGES.map[keyFor(look)];
      var art = entry ? imageArt(entry, o) : DRAW[fam(look.shape)](look, o);
      return { svg: art.svg, w: W, h: H, ax: art.ax, ay: art.ay };
    }

    /** A stand-alone picture of a connector for a picker tile. */
    function connectorSvg(look, opts) {
      var o = opts || {};
      var art = connector(look, o);
      var title = o.title ? "<title>" + esc(o.title) + "</title>" : "";
      // a short stub of cable so the connector reads as one on the end of a cable
      var entry = IMAGES.map[keyFor(look)];
      var stub = entry
        ? ""
        : art.ax
          ? '<rect x="' +
            (art.ax - 6) +
            '" y="' +
            (art.ay - 8) +
            '" width="12" height="10" fill="' +
            (o.cable || "#1d1d1d") +
            '"/>'
          : '<rect x="-4" y="' +
            (art.ay - 6) +
            '" width="14" height="12" fill="' +
            (o.cable || "#1d1d1d") +
            '"/>';
      return (
        '<svg viewBox="' +
        tileBox(entry) +
        '" role="img" aria-hidden="' +
        (o.title ? "false" : "true") +
        '" focusable="false">' +
        title +
        stub +
        art.svg +
        "</svg>"
      );
    }

    /**
     * The part of the picture a tile shows. A supplied picture is cropped to the
     * drawing itself (from the manifest's bbox) so long thin parts and small
     * panel parts fill their tile; a part never zooms in more than 2.5 times.
     */
    function tileBox(entry) {
      var b = entry && entry.bbox;
      if (!b) return "-6 0 162 100";
      var pad = 4;
      var x0 = b.x0 * W - pad;
      var x1 = b.x1 * W + pad;
      var y0 = b.y0 * H - pad;
      var y1 = b.y1 * H + pad;
      var w = Math.max(x1 - x0, 60);
      var h = Math.max(y1 - y0, 40);
      var aspect = 1.5;
      if (w / h < aspect) w = h * aspect;
      else h = w / aspect;
      var cx = (x0 + x1) / 2;
      var cy = (y0 + y1) / 2;
      return (
        Math.round((cx - w / 2) * 100) / 100 +
        " " +
        Math.round((cy - h / 2) * 100) / 100 +
        " " +
        Math.round(w * 100) / 100 +
        " " +
        Math.round(h * 100) / 100
      );
    }

    /* ---------- whole cables ---------- */

    function fmtLen(m) {
      return Math.round(m * 100) / 100 + "\u00a0m";
    }

    function place(art, x, y, mirror, scale) {
      var sx = (mirror ? -1 : 1) * scale;
      var tx = x - (art.ax || 0) * sx;
      return (
        '<g transform="translate(' +
        tx +
        "," +
        (y - art.ay * scale) +
        ") scale(" +
        sx +
        "," +
        scale +
        ')">' +
        art.svg +
        "</g>"
      );
    }

    function tag(x, y, text, anchor) {
      if (!text) return "";
      var w = Math.max(30 * TS, (text.length * 6.4 + 14) * TS);
      var rx = anchor === "end" ? x - w : anchor === "middle" ? x - w / 2 : x;
      topMost = Math.min(topMost, y - 11 * TS);
      return (
        '<g><rect x="' +
        rx +
        '" y="' +
        (y - 11 * TS) +
        '" width="' +
        w +
        '" height="' +
        20 * TS +
        '" rx="4" fill="#fff" stroke="#bdb6a2"/>' +
        '<text x="' +
        (rx + w / 2) +
        '" y="' +
        (y + 3.5 * TS) +
        '" text-anchor="middle" font-size="' +
        11 * TS +
        '" font-weight="700" fill="' +
        INK +
        '">' +
        esc(text) +
        "</text></g>"
      );
    }

    /** A channel's printed label, cut to fit its column; the full text is in the summary. */
    function smallTag(x, y, text, maxW) {
      if (!text) return "";
      var k = Math.min(TS, 1.6);
      var chars = Math.max(3, Math.floor((maxW - 12) / (5.8 * k)));
      var shown =
        text.length > chars ? text.slice(0, chars - 1) + "\u2026" : text;
      var w = Math.min(maxW, (shown.length * 5.8 + 12) * k);
      return (
        '<g><rect x="' +
        (x - w / 2) +
        '" y="' +
        (y - 8 * k) +
        '" width="' +
        w +
        '" height="' +
        16 * k +
        '" rx="3" fill="#fff" stroke="#bdb6a2"/>' +
        '<text x="' +
        x +
        '" y="' +
        (y + 3.5 * k) +
        '" text-anchor="middle" font-size="' +
        10 * k +
        '" font-weight="700" fill="' +
        INK +
        '">' +
        esc(shown) +
        "</text></g>"
      );
    }

    function note(x, y, text) {
      return (
        '<text x="' +
        x +
        '" y="' +
        y +
        '" text-anchor="middle" font-size="' +
        10 * TS +
        '" fill="#5d5d5d">' +
        esc(text) +
        "</text>"
      );
    }

    /** Small text that stays readable over legs and connector backs. */
    function haloNote(x, y, text, anchor, size, weight, fixed) {
      return (
        '<text x="' +
        x +
        '" y="' +
        y +
        '" text-anchor="' +
        (anchor || "middle") +
        '" font-size="' +
        (size || 10) * (fixed ? 1 : TS) +
        '"' +
        (weight ? ' font-weight="' + weight + '"' : "") +
        ' fill="#3d3d3d" stroke="#f5f3ed" stroke-width="3" stroke-linejoin="round" paint-order="stroke">' +
        esc(text) +
        "</text>"
      );
    }

    // slot key -> "check" | "note", for the diagram being drawn
    var flagged = {};
    // a compact picture sits inside a button or a basket line, so it has no targets of its own
    var inert = false;
    // text grows with this when the picture is drawn small, so labels stay legible on a phone
    var TS = 1;
    // the highest point a tag reaches; large text can lift one above the top edge
    var topMost = 0;

    function hot(slot, inner, label) {
      if (!slot || inert) return inner;
      var level = flagged[String(slot).split("#")[0]];
      return (
        '<g class="ca-hot' +
        (level === "check" ? " ca-hot--check" : "") +
        '" data-slot="' +
        esc(slot) +
        '" tabindex="0" role="button" aria-label="' +
        esc(label || "Change") +
        '">' +
        inner +
        "</g>"
      );
    }

    function runLine(x0, y0, x1, y1, colour, width) {
      return (
        '<path d="M' +
        x0 +
        "," +
        y0 +
        " L" +
        x1 +
        "," +
        y1 +
        '" stroke="' +
        colour +
        '" stroke-width="' +
        width +
        '" stroke-linecap="round" fill="none"/>' +
        '<path d="M' +
        x0 +
        "," +
        (y0 - width / 2 + 2) +
        " L" +
        x1 +
        "," +
        (y1 - width / 2 + 2) +
        '" stroke="#fff" stroke-opacity=".16" stroke-width="2" stroke-linecap="round" fill="none"/>'
      );
    }

    function stagebox(ways, look, sockets, x, y, w, h, socketSlot) {
      var cols = ways <= 8 ? ways : ways <= 16 ? 8 : 12;
      var rows = Math.ceil(ways / cols);
      var s =
        '<rect x="' +
        x +
        '" y="' +
        y +
        '" width="' +
        w +
        '" height="' +
        h +
        '" rx="8" fill="url(#ca-black)" stroke="#000" stroke-width="1.2"/>';
      s +=
        '<rect x="' +
        (x + 5) +
        '" y="' +
        (y + 5) +
        '" width="' +
        (w - 10) +
        '" height="' +
        (h - 10) +
        '" rx="5" fill="none" stroke="#fff" stroke-opacity=".12"/>';
      var gx = (w - 20) / cols;
      var gy = (h - 22) / rows;
      for (var i = 0; i < ways; i++) {
        var cx = x + 10 + gx * (i % cols) + gx / 2;
        var cy = y + 11 + gy * Math.floor(i / cols) + gy / 2;
        var look2 = sockets && sockets[i] ? sockets[i] : look;
        var r = Math.min(gx, gy) / 2 - 2;
        var socket =
          circ(cx, cy, r, "#2c2c2c", "#9aa0a6", 1.2) +
          circ(cx, cy, r * 0.62, "#070707", "#555", 0.8);
        if (look2 && look2.finish !== "black")
          socket +=
            '<circle cx="' +
            cx +
            '" cy="' +
            cy +
            '" r="' +
            (r + 0.8) +
            '" fill="none" stroke="#d4d8dc" stroke-width="1"/>';
        s += socketSlot
          ? hot(socketSlot + "#" + (i + 1), socket, "Change socket " + (i + 1))
          : socket;
      }
      return s;
    }

    /**
     * model = {
     *   run: { label, colour, legs?: [labels] },
     *   left:  terminal, right: terminal
     * }
     * terminal =
     *   { type:'connector', look, boot, ring, label, slot }
     *   { type:'fan', count, items?:[item], look, boot, label, tailLabel, slot, tailSlot }
     *   { type:'stagebox', ways, look, sockets, slot, socketSlot }
     *   { type:'legs', legs:[item], look, slot, joint? }   (breakout or split)
     *   { type:'empty', label, slot, title }   (an end not chosen yet)
     * run.empty draws the cable dashed, for a design with no cable chosen.
     * item = { look, boot, ring, label, length, slot, title }: a fan draws
     * every channel it is given, each with its own connector, colours and
     * label; without `items` every channel is drawn as `look`.
     * Returns an <svg> string. Elements with data-slot are click targets.
     */
    function diagram(model, opts) {
      var o = opts || {};
      var compact = !!o.compact;
      inert = compact;
      flagged = o.flags || {};
      TS = compact ? 1 : Math.max(1, Math.min(3.8, o.textScale || 1));
      topMost = 0;
      var captions =
        !compact &&
        !o.noCaptions &&
        !!(
          (model.left && model.left.caption) ||
          (model.right && model.right.caption)
        );
      var width = 760;
      var cableColour = (model.run && model.run.colour) || "#1b1b1b";
      var parts = "";
      var heightNeeded = 190;
      // Cables are drawn at their real thickness: the overall diameter VDC
      // publishes, at the same scale as the connectors (1.25 units per mm at
      // 15 px/mm, times the diagram scale).
      var UNITS_PER_MM = 1.25;
      var thick = function (mm, factor, min, max) {
        return Math.max(min, Math.min(max, mm * UNITS_PER_MM * factor));
      };

      var simple = function (t) {
        return t && (t.type === "connector" || t.type === "multipin");
      };
      var drawn = function (t) {
        return simple(t) && hasImage(t.look);
      };
      // supplied pictures are true to scale, so the connectors fill less of
      // their box: draw them a little larger
      var scale =
        simple(model.left) && simple(model.right)
          ? drawn(model.left) && drawn(model.right)
            ? 1.45
            : 1.2
          : 0.9;
      var LEG_REACH = 56;
      var LABEL_COL = 92;
      /** How a fan or a set of legs is laid out: every channel, drawn smaller as there are more. */
      function spreadOf(t) {
        var items =
          t.type === "legs"
            ? t.legs
            : t.items && t.items.length
              ? t.items
              : Array.apply(null, Array(Math.max(0, t.count || 0))).map(
                  function () {
                    return { look: t.look, boot: t.boot };
                  },
                );
        items = items.map(function (it) {
          return it || {};
        });
        var n = items.length;
        var stagger = n > 12;
        var pitch =
          n <= 5
            ? 46
            : n <= 8
              ? 38
              : n <= 12
                ? 30
                : n <= 16
                  ? 24
                  : n <= 24
                    ? 19
                    : n <= 32
                      ? 15
                      : 12;
        var room = stagger ? pitch * 2 : pitch;
        var base = n && hasImage(items[0].look) ? 0.8 : 0.62;
        var s = Math.min(base, base * (room / 46) * 1.1);
        var reach = W * s + 6;
        // a small card has no room for a label column, so the labels are left to the full picture
        var labelled =
          !compact &&
          !(o.labelsInLegend && t.type === "fan" && n > 1) &&
          items.some(function (it) {
            return !!it.label;
          });
        var lengths = items.some(function (it) {
          return !!it.length;
        });
        var lead = LEG_REACH + (lengths ? 18 : 0);
        // channel numbers stand in their own column beyond the connectors, clear of the legs
        var numbered =
          t.type === "fan" && !compact && n > 1 && !lengths && !o.noNumbers;
        // many channels drawn small: every other number, at twice the room, so each one can be read
        var sparse = numbered && n > 12 && TS * 10 > pitch - 4;
        var numSize = sparse
          ? 10 * Math.min(TS, (pitch * 2 - 4) / 10)
          : (pitch < 16 ? 8 : 10) * Math.min(TS, Math.max(1, (pitch - 4) / 10));
        var numCol = numbered
          ? Math.ceil(String(n).length * 0.62 * numSize + 8)
          : 0;
        return {
          items: items,
          n: n,
          pitch: pitch,
          stagger: stagger,
          s: s,
          reach: reach,
          lead: lead,
          labelled: labelled,
          numbered: numbered,
          sparse: sparse,
          numSize: numSize,
          w:
            lead +
            reach * (stagger ? 2 : 1) +
            (labelled ? LABEL_COL : 0) +
            numCol +
            4,
          h: Math.max(190, n * pitch + 76),
        };
      }
      function sizeOf(t) {
        if (!t) return { w: 0, h: 0 };
        if (t.type === "fan" || t.type === "legs") {
          var g = spreadOf(t);
          return { w: g.w, h: g.h };
        }
        if (t.type === "stagebox") return { w: 210, h: 214 };
        return { w: 150 * scale + 6, h: 190 };
      }
      var ls = sizeOf(model.left);
      var rs = sizeOf(model.right);
      heightNeeded = Math.max(ls.h, rs.h, 190);
      // tight: a plain two-ended cable drawn with a short run, so its connectors fill a small frame
      // the tags beside a fan reach into the run, and grow with the text, so the run grows with them
      var tagW = function (text) {
        return Math.max(30 * TS, (String(text).length * 6.4 + 14) * TS);
      };
      var intrude = function (t) {
        if (!t || (t.type !== "fan" && t.type !== "legs") || compact) return 0;
        var n = t.items && t.items.length ? t.items.length : t.count || 0;
        var summary = t.type === "fan" ? "\u00d7" + n : t.joint || t.label;
        return Math.max(
          summary ? tagW(summary) + 12 : 0,
          t.tailLabel ? tagW(t.tailLabel) / 2 - 44 : 0,
        );
      };
      var runRoom = Math.max(
        240,
        intrude(model.left) + intrude(model.right) + 24,
        model.run && model.run.label ? 124 * Math.min(TS, 2.6) + 40 : 0,
      );
      width =
        (o.tight || compact) && simple(model.left) && simple(model.right)
          ? Math.max(440, ls.w + rs.w + 180)
          : Math.max(width, ls.w + rs.w + runRoom);
      // a right-angle connector reaches up from the cable line, so make room
      var maxUp = 0;
      var maxDown = 62;
      [model.left, model.right].forEach(function (t) {
        if (!simple(t)) return;
        var a = connector(t.look, { trueScale: true });
        maxUp = Math.max(maxUp, a.ay * scale);
        maxDown = Math.max(maxDown, (H - a.ay) * scale);
      });
      var cy = Math.max(heightNeeded / 2, maxUp + 10 + (TS - 1) * 26);
      heightNeeded = Math.max(heightNeeded, cy + maxDown + 28);
      var runW =
        model.run && model.run.odMm ? thick(model.run.odMm, scale, 5, 44) : 9;
      // the cable's name sits under the run; a joint's tag goes under that, never on it
      var subY = cy + Math.max(28, runW / 2 + 16) + (TS - 1) * 9;
      var jointY = subY + 10 + 11 * TS;
      var lowest = Math.max(
        cy + 62 + 9 * TS,
        model.run && model.run.sub ? jointY + 9 * TS : 0,
      );
      heightNeeded = Math.max(heightNeeded, lowest + 10);
      var captionY = heightNeeded + 8;
      if (captions) heightNeeded += 22;
      // a frame shorter than the drawing's shape: lengthen the run so the ends reach its sides
      if (o.minWidth && !compact)
        width = Math.max(width, Math.round(o.minWidth));
      var lx = 8 + ls.w;
      var rx = width - 8 - rs.w;

      /** "End 1 · XLR 3 pin female" under an end, so the steps can be matched to the picture. */
      function caption(t, x, side) {
        if (!captions || !t || !t.caption) return "";
        var room = side === "left" ? x - 6 : width - 6 - x;
        var chars = Math.max(10, Math.floor((room * 2) / 6.2));
        var text =
          t.caption.length > chars
            ? t.caption.slice(0, chars - 1) + "\u2026"
            : t.caption;
        var anchor = "middle";
        var half = (text.length * 6.2) / 2;
        if (x - half < 6) {
          anchor = "start";
          x = 6;
        } else if (x + half > width - 6) {
          anchor = "end";
          x = width - 6;
        }
        return haloNote(x, captionY, text, anchor, 11, 600);
      }

      function end(t, side) {
        if (!t) return "";
        var mirror = side === "left";
        var out = "";
        if (t.type === "empty") {
          var bw = 128;
          var bx = mirror ? lx - bw - 4 : rx + 4;
          out +=
            '<rect x="' +
            bx +
            '" y="' +
            (cy - 38) +
            '" width="' +
            bw +
            '" height="76" rx="10" fill="#fbfaf6" stroke="#8a8372" stroke-width="1.6" stroke-dasharray="6 5"/>' +
            '<text x="' +
            (bx + bw / 2) +
            '" y="' +
            (cy - 4) +
            '" text-anchor="middle" font-size="26" font-weight="700" fill="#8a8372">+</text>' +
            '<text x="' +
            (bx + bw / 2) +
            '" y="' +
            (cy + 20) +
            '" text-anchor="middle" font-size="12" font-weight="700" fill="#3d3d3d">' +
            esc(t.label || "Choose a connector") +
            "</text>";
          return (
            hot(t.slot, out, t.title || "Choose a connector") +
            caption(t, bx + bw / 2, side)
          );
        }
        if (t.type === "connector") {
          var art = connector(t.look, {
            boot: t.boot,
            ring: t.ring,
            trueScale: true,
          });
          out += place(art, mirror ? lx - 0 : rx, cy, mirror, scale);
          var tagX = mirror
            ? lx - (art.w * scale) / 2
            : rx + (art.w * scale) / 2;
          out += tag(tagX, cy + 62, t.label, "middle");
          return (
            hot(t.slot, out, t.title || "Change this connector") +
            caption(t, tagX, side)
          );
        }
        if (t.type === "multipin") {
          var mp = connector(t.look, { boot: t.boot, trueScale: true });
          out += place(mp, mirror ? lx : rx, cy, mirror, scale);
          var mpX = mirror ? lx - 70 * scale : rx + 70 * scale;
          out += tag(mpX, cy + 66, t.label, "middle");
          return (
            hot(t.slot, out, t.title || "Change this connector") +
            caption(t, mpX, side)
          );
        }
        if (t.type === "stagebox") {
          var sbx = mirror ? 8 : rx;
          out += stagebox(
            t.ways,
            t.look,
            t.sockets,
            sbx,
            cy - 80,
            210,
            160,
            t.socketSlot,
          );
          out += tag(sbx + 105, cy + 96, t.label || t.ways + " way", "middle");
          out +=
            '<path d="M' +
            (mirror ? sbx + 210 : sbx) +
            "," +
            cy +
            " h" +
            (mirror ? 4 : -4) +
            '" stroke="#000"/>';
          return (
            hot(t.slot, out, t.title || "Change the stage box") +
            caption(t, sbx + 105, side)
          );
        }
        if (t.type === "fan" || t.type === "legs") {
          var g = spreadOf(t);
          var dir = mirror ? -1 : 1;
          var jx = mirror ? lx : rx;
          var y0 = cy - ((g.n - 1) * g.pitch) / 2;
          var legW = thick(6, g.s, 2.5, 14);
          var legColour = t.type === "legs" ? cableColour : INK;
          // groupHot: channels drawn too close to tap one by one share the end's target
          var own =
            !o.groupHot &&
            g.items.some(function (it) {
              return !!it.slot;
            });
          var legs = "";
          var heads = "";
          g.items.forEach(function (it, i) {
            var y = g.n > 1 ? y0 + g.pitch * i : cy;
            var col = g.stagger && i % 2 === 1 ? 1 : 0;
            var ex = jx + dir * (g.lead + col * g.reach);
            legs +=
              '<path d="M' +
              jx +
              "," +
              cy +
              " C" +
              (jx + dir * 28) +
              "," +
              cy +
              " " +
              (ex - dir * 26) +
              "," +
              y +
              " " +
              ex +
              "," +
              y +
              '" stroke="' +
              legColour +
              '" stroke-width="' +
              legW +
              '" fill="none" stroke-linecap="round"/>';
            var piece = place(
              connector(it.look, {
                boot: it.boot,
                ring: it.ring,
                trueScale: true,
              }),
              ex,
              y,
              mirror,
              g.s,
            );
            // leg lengths sit on the outer side of their leg, clear of its curve,
            // and stop short of the connector
            if (it.length) {
              var below = g.n > 1 && y > cy;
              piece += haloNote(
                ex - dir * 18,
                below
                  ? y + legW / 2 + 4 + 9 * Math.min(TS, 1.8)
                  : y - legW / 2 - 6,
                it.length,
                mirror ? "start" : "end",
                10 * Math.min(TS, 1.8),
                null,
                true,
              );
            } else if (g.numbered && (!g.sparse || i % 2 === 0)) {
              piece += haloNote(
                jx +
                  dir *
                    (g.lead +
                      g.reach * (g.stagger ? 2 : 1) +
                      (g.labelled ? LABEL_COL : 0) +
                      4),
                y + g.numSize * 0.36,
                String(i + 1),
                mirror ? "end" : "start",
                g.numSize,
                "700",
                true,
              );
            }
            if (g.labelled && it.label)
              piece += smallTag(
                jx +
                  dir *
                    (g.lead + g.reach * (g.stagger ? 2 : 1) + LABEL_COL / 2),
                y,
                it.label,
                LABEL_COL - 8,
              );
            heads += own
              ? hot(it.slot, piece, it.title || "Change channel " + (i + 1))
              : piece;
          });
          out += legs;
          out +=
            '<rect x="' +
            (jx + (mirror ? -16 : 0)) +
            '" y="' +
            (cy - legW - 4) +
            '" width="16" height="' +
            (legW * 2 + 8) +
            '" rx="4" fill="#2a2a2a" stroke="#000" stroke-width="1"/>';
          out += heads;
          var summary =
            t.type === "fan" ? "\u00d7" + g.n : t.joint || t.label || "";
          // beside the fan, on the cable side of the joint
          var foot = compact
            ? ""
            : tag(
                jx - dir * 12,
                model.run &&
                  model.run.label &&
                  model.run.sub &&
                  !o.noCaptions &&
                  !compact
                  ? Math.max(cy + Math.max(26, legW + 18), jointY)
                  : cy + Math.max(26, legW + 18) + (TS - 1) * 10,
                summary,
                mirror ? "start" : "end",
              );
          var top =
            t.tailLabel && !compact
              ? tag(
                  jx + dir * 44,
                  y0 - 22 - (TS - 1) * 10,
                  t.tailLabel,
                  "middle",
                )
              : "";
          var under = caption(t, jx + dir * (g.lead + g.reach / 2), side);
          if (!own)
            return (
              hot(
                t.slot,
                out + foot + top,
                t.title || "Change these connectors",
              ) + under
            );
          return (
            out +
            hot(t.slot, foot, t.title || "Change these connectors") +
            hot(t.tailSlot || t.slot, top, t.tailTitle || "Change the tails") +
            under
          );
        }
        return "";
      }

      var x0 = lx;
      var x1 = rx;
      var runColour = cableColour;
      // The cable runs a little way under a straight connector, so a part with
      // an open clamp (the EDAC and DL2 covers) shows it through the window. Fans,
      // right-angle parts and stage boxes join at their own point.
      var under = function (t) {
        if (!simple(t)) return 2;
        return Math.abs(connector(t.look, {}).ay - 50) < 1 ? 26 : 2;
      };
      parts +=
        model.run && model.run.empty
          ? '<path d="M' +
            (x0 - under(model.left)) +
            "," +
            cy +
            " L" +
            (x1 + under(model.right)) +
            "," +
            cy +
            '" stroke="#8a8372" stroke-width="4" stroke-dasharray="10 8" stroke-linecap="round" fill="none"/>'
          : runLine(
              x0 - under(model.left),
              cy,
              x1 + under(model.right),
              cy,
              runColour,
              runW,
            );
      if (model.run && model.run.label && !o.noPill) {
        var mid = (x0 + x1) / 2;
        var pk = Math.min(TS, 2.6, Math.max(0.8, (x1 - x0 - 30) / 124));
        var pillW = 124 * pk;
        var pillH = 26 * pk;
        var pillTop = cy - Math.max(38, runW / 2 + 24) - (pillH - 26);
        parts += hot(
          model.run.slot,
          '<rect x="' +
            (mid - pillW / 2) +
            '" y="' +
            pillTop +
            '" width="' +
            pillW +
            '" height="' +
            pillH +
            '" rx="' +
            pillH / 2 +
            '" fill="#e9ad14" stroke="#b9860a"/>' +
            '<text x="' +
            mid +
            '" y="' +
            (pillTop + pillH * 0.69) +
            '" text-anchor="middle" font-size="' +
            14 * pk +
            '" font-weight="800" fill="#1b1b1b">' +
            esc(model.run.label) +
            "</text>",
          model.run.title || "Change the length",
        );
      }
      if (
        model.run &&
        model.run.label &&
        model.run.sub &&
        !o.noCaptions &&
        !compact
      )
        parts +=
          '<text x="' +
          (x0 + x1) / 2 +
          '" y="' +
          subY +
          '" text-anchor="middle" font-size="' +
          11 * TS +
          '" fill="#5d5d5d">' +
          esc(model.run.sub) +
          "</text>";
      parts += end(model.left, "left") + end(model.right, "right");

      var label = esc(model.alt || "Diagram of your cable");
      var h = heightNeeded + (TS - 1) * 26;
      var vy = Math.min(0, Math.floor(topMost - 4));
      flagged = {};
      inert = false;
      TS = 1;
      return (
        '<svg class="ca-diagram' +
        (compact ? " ca-diagram--compact" : "") +
        '" viewBox="0 ' +
        vy +
        " " +
        width +
        " " +
        (h - vy) +
        (compact
          ? '" aria-hidden="true'
          : '" role="group" aria-label="' + label) +
        '" focusable="false">' +
        parts +
        "</svg>"
      );
    }

    /** A stage box face on its own, for choosing a size. */
    function stageboxSvg(ways) {
      var rows = Math.ceil(ways / (ways <= 8 ? ways : ways <= 16 ? 8 : 12));
      var h = 34 + rows * 22;
      return (
        '<svg class="ca-box" viewBox="0 0 210 ' +
        (h + 8) +
        '" aria-hidden="true" focusable="false">' +
        stagebox(ways, null, null, 4, 4, 202, h, null) +
        "</svg>"
      );
    }

    return {
      defs: defs,
      stageboxSvg: stageboxSvg,
      connector: connector,
      connectorSvg: connectorSvg,
      keyFor: keyFor,
      useImages: useImages,
      hasImage: hasImage,
      diagram: diagram,
      colourOf: colourOf,
      fmtLen: fmtLen,
      shapes: Object.keys(DRAW),
    };
  },
);
